import { useEffect, useRef, useState } from 'react';

import { Box, Container, Flex, Stack, Text, Title } from '@mantine/core';

import { TierStrataCard } from '@/components/molecules';
import { T } from '@/theme/assay';
import { TIERS_BY_PRIORITY } from '@/types/grade';

const STRATA_LINES = [
  { y: 0.18, opacity: 0.05 },
  { y: 0.35, opacity: 0.08 },
  { y: 0.5, opacity: 0.22 },
  { y: 0.66, opacity: 0.08 },
  { y: 0.84, opacity: 0.05 },
];

// ─── Section ─────────────────────────────────────────────────────────────────
// Mirrors Scene 2 of the marketing animations design - Assay's signature
// four-tier strata visual rises in once the section enters the viewport.

export function TierStrataSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  // Default to inView in environments without IntersectionObserver (tests, SSR)
  // so the section content is visible without needing to scroll.
  const [inView, setInView] = useState(
    () => typeof globalThis.window === 'undefined' || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={sectionRef}
      style={{
        position: 'relative',
        paddingTop: 64,
        paddingBottom: 64,
        overflow: 'hidden',
      }}
    >
      {/* Decorative strata lines, left edge - drawn in once the section appears */}
      <Box
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 160,
          pointerEvents: 'none',
        }}
      >
        {STRATA_LINES.map((line, i) => (
          <Box
            key={line.y}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${line.y * 100}%`,
              height: 1,
              background: `rgba(196, 152, 58, ${line.opacity})`,
              transformOrigin: 'left',
              transform: inView ? undefined : 'scaleX(0)',
              animation: inView
                ? `assay-strata-line-draw 1.4s cubic-bezier(0.22, 1, 0.36, 1) ${0.2 + i * 0.08}s both`
                : undefined,
            }}
          />
        ))}
      </Box>

      <Container size="lg">
        <Stack gap={48}>
          <Stack gap={16} maw={680}>
            <Flex align="center" gap={10}>
              <Box
                style={{
                  width: 32,
                  height: 1,
                  background: T.ore,
                  transformOrigin: 'left',
                  transform: inView ? undefined : 'scaleX(0)',
                  animation: inView
                    ? 'assay-eyebrow-rule-draw 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both'
                    : undefined,
                }}
              />
              <Text
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.6rem',
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: T.ore,
                  opacity: 0,
                  animation: inView ? 'assay-word-rise 0.55s ease 0.4s both' : undefined,
                }}
              >
                The four tiers
              </Text>
            </Flex>

            <Title
              order={2}
              style={{
                margin: 0,
                opacity: 0,
                animation: inView ? 'assay-word-rise 0.6s ease 0.55s both' : undefined,
              }}
            >
              Your network, in strata.
            </Title>

            <Text
              size="sm"
              c="dimmed"
              style={{
                opacity: 0,
                maxWidth: 620,
                animation: inView ? 'assay-word-rise 0.6s ease 0.7s both' : undefined,
              }}
            >
              Everyone you know, sorted by how close you are. Each tier sets how often Assay reminds
              you to reach out - so your closest relationships never go cold, and the rest never get
              forgotten.
            </Text>
          </Stack>

          <Flex
            gap={16}
            wrap="wrap"
            justify={{ base: 'flex-start', sm: 'space-between' }}
            style={{ rowGap: 24 }}
          >
            {TIERS_BY_PRIORITY.map((meta, idx) => (
              <TierStrataCard key={meta.value} tier={meta.value} index={idx} inView={inView} />
            ))}
          </Flex>
        </Stack>
      </Container>
    </Box>
  );
}
